import React, { useState, useEffect } from 'react';
import imageBannerService, { ImageBanner } from '../services/imageBannerService';
import './ImageCarousel.css';

interface ImageCarouselProps {
  className?: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ className = '' }) => {
  const [imageBanners, setImageBanners] = useState<ImageBanner[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<ImageBanner | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchImageBanners();
  }, []);

  const fetchImageBanners = async () => {
    try {
      setLoading(true);
      const banners = await imageBannerService.getImageBanners();
      setImageBanners(banners);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch image banners:', err);
      setError('Failed to load carousel images');
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === imageBanners.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? imageBanners.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const openImagePopup = (image: ImageBanner) => {
    setSelectedImage(image);
  };

  const closeImagePopup = () => {
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <div className={`image-carousel ${className}`}>
        <div className="carousel-loading">Loading carousel...</div>
      </div>
    );
  }

  if (error || imageBanners.length === 0) {
    return (
      <div className={`image-carousel ${className}`}>
        <div className="carousel-error">
          {error || 'No images available'}
        </div>
      </div>
    );
  }

  const currentImage = imageBanners[currentIndex];

  return (
    <>
      <div className={`image-carousel ${className}`}>
        <div className="carousel-container">
          {/* Main Image */}
          <div className="carousel-slide">
            <img
              src={currentImage.imageUrl}
              alt={currentImage.title}
              className="carousel-image"
              onClick={() => openImagePopup(currentImage)}
            />
            <div className="carousel-overlay">
              <div className="carousel-content">
                <h3 className="carousel-title">{currentImage.title}</h3>
                <p className="carousel-description">
                  {currentImage.description}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          {imageBanners.length > 1 && (
            <>
              <button 
                className="carousel-nav carousel-prev" 
                onClick={prevSlide}
                aria-label="Previous image"
              >
                &#8249;
              </button>
              <button 
                className="carousel-nav carousel-next" 
                onClick={nextSlide}
                aria-label="Next image"
              >
                &#8250;
              </button>
            </>
          )}

          {/* Dots Indicator */}
          {imageBanners.length > 1 && (
            <div className="carousel-dots">
              {imageBanners.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Image Popup Modal */}
      {selectedImage && (
        <div className="image-popup-overlay" onClick={closeImagePopup}>
          <div className="image-popup-container">
            <button 
              className="image-popup-close" 
              onClick={closeImagePopup}
              aria-label="Close image"
            >
              &times;
            </button>
            <img
              src={selectedImage.imageUrl}
              alt={selectedImage.title}
              className="image-popup-image"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="image-popup-info">
              <h3 className="image-popup-title">{selectedImage.title}</h3>
              {selectedImage.description && (
                <p className="image-popup-description">
                  {selectedImage.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageCarousel;
